import axios from 'axios';
import { insertMessage, mergeMessages } from '@/utils/chatMessages';
import {
  CHAT_ATTACH_FILE,
  CHAT_CLEAR_ATTACHMENTS,
  CHAT_CONFIG_REQUEST,
  CHAT_CONVERSATIONS_DETAILS_REQUEST,
  CHAT_DELETE_ATTACHMENT,
  CHAT_MESSAGE_SEND_REQUEST,
  CHAT_MESSAGES_READ_REQUEST,
  CHAT_MESSAGES_REQUEST,
  CHAT_MESSAGES_REQUEST_STARTED,
  CHAT_MESSAGES_RESET,
  CHAT_WEBSOCKET_MESSAGE_READ_RECEIVED,
  CHAT_WEBSOCKET_RECEIVED,
} from '@/store/actions/chat';

const state = {
  chatConversationChannel: null,
  chatConversationId: '',
  chatWebSocketURL: null,
  chatMessages: [],
  // Every /message/list request takes the next sequence number; only the highest one
  // applied so far may overwrite message fields, so a slow older response cannot
  // revert what a newer one already wrote. Both counters are monotonic for the life of
  // the session - a reset bumps the epoch instead, which discards responses that belong
  // to a conversation the user has already left.
  chatMessagesSeq: 0,
  chatMessagesAppliedSeq: 0,
  chatMessagesEpoch: 0,
  chatAttachedFiles: [],
};

const getters = {
  chatConversationChannel: () => state.chatConversationChannel,
  chatConversationId: () => state.chatConversationId,
  chatWebSocketURL: () => state.chatWebSocketURL,
  chatMessages: () => state.chatMessages,
  chatAttachedFiles: () => state.chatAttachedFiles,
};

const actions = {
  /**
   * Get chat config request (web socket url)
   * @returns {Promise}
   */
  [CHAT_CONFIG_REQUEST]({ commit }, payload) {
    return axios.post(`/${payload.isHrChat ? 'company' : 'insurance'}/conversation/config/detail`)
      .then((resp) => {
        commit(CHAT_CONFIG_REQUEST, resp);
        return resp.data.connectUrl;
      });
  },
  /**
   * Get conversation details (channel and id) request
   * @returns {Promise<void>}
   */
  [CHAT_CONVERSATIONS_DETAILS_REQUEST]({ commit, state }, payload) {
    const epoch = state.chatMessagesEpoch;
    return axios.post(`/${payload.isHrChat ? 'company' : 'insurance'}/conversation/detail`, {})
      .then((resp) => {
        // A chat closed while this was in flight must not overwrite the conversation
        // the user has since opened.
        if (epoch === state.chatMessagesEpoch) {
          commit(CHAT_CONVERSATIONS_DETAILS_REQUEST, resp);
        }
        return resp.data;
      });
  },
  /**
   * Send message request
   * @param commit
   * @param dispatch
   * @param state
   * @param payload
   * @returns {Promise}
   */
  [CHAT_MESSAGE_SEND_REQUEST]({ commit, dispatch, state }, payload) {
    return axios.post(`/${payload.isHrChat ? 'company' : 'insurance'}/conversation/message/send`, {
      cdnFileIds: state.chatAttachedFiles.map((file) => file.cdnFileId),
      conversationId: payload.conversationId,
      text: payload.message,
    }).then((resp) => {
      commit(CHAT_MESSAGE_SEND_REQUEST, resp.data);
      return resp.data;
    });
  },
  [CHAT_CLEAR_ATTACHMENTS]({ commit }) {
    commit(CHAT_CLEAR_ATTACHMENTS);
  },
  /**
   * API request for a page of chat message history. The result is merged into the
   * current list instead of replacing it, so messages pushed by the websocket while
   * the request was in flight survive.
   *
   * Callers are responsible for marking messages read - this action never does it.
   *
   * @param commit
   * @param state
   * @param payload {conversationId: string, lastNumber: number, isHrChat: boolean}
   * @returns {Promise<{messages: Array, applied: boolean}>}
   */
  [CHAT_MESSAGES_REQUEST]({ commit, state }, payload) {
    const seq = state.chatMessagesSeq + 1;
    const epoch = state.chatMessagesEpoch;
    commit(CHAT_MESSAGES_REQUEST_STARTED, seq);
    const { conversationId } = payload;
    return axios.post(`/${payload.isHrChat ? 'company' : 'insurance'}/conversation/message/list`, {
      conversationId,
      to: payload.lastNumber,
    }).then((resp) => {
      const messages = resp.data.messages || [];
      // The chat was reset or re-opened for another conversation while this was in flight.
      if (epoch !== state.chatMessagesEpoch || conversationId !== state.chatConversationId) {
        return { messages: [], applied: false };
      }
      commit(CHAT_MESSAGES_REQUEST, {
        messages,
        seq,
        fresh: seq > state.chatMessagesAppliedSeq,
      });
      return { messages, applied: true };
    });
  },
  [CHAT_MESSAGES_READ_REQUEST]({ commit }, payload) {
    return axios.post(`/${payload.isHrChat ? 'company' : 'insurance'}/conversation/message/mark-read`, {
      conversationId: payload.conversationId,
    }).then(() => {
      commit(CHAT_MESSAGES_READ_REQUEST);
    });
  },
  [CHAT_ATTACH_FILE]({ commit, dispatch }, payload) {
    // upload file Multipart/form-data
    const formData = new FormData();
    formData.append('file', payload.file);
    return axios.post(`/${payload.isHrChat ? 'company' : 'insurance'}/conversation/file/save`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((resp) => {
      commit(CHAT_ATTACH_FILE, resp.data);
      return resp.data;
    });
  },
};

const mutations = {
  [CHAT_MESSAGE_SEND_REQUEST]: (state) => {
    // Will clear attached files after sending message
    state.chatAttachedFiles = [];
  },
  [CHAT_CONFIG_REQUEST]: (state, resp) => {
    state.chatWebSocketURL = resp.data.connectUrl;
  },
  [CHAT_CONVERSATIONS_DETAILS_REQUEST]: (state, resp) => {
    state.chatConversationChannel = resp.data.conversationChannel;
    state.chatConversationId = resp.data.conversationId;
  },
  [CHAT_WEBSOCKET_RECEIVED]: (state, data) => {
    state.chatMessages = insertMessage(state.chatMessages, data);
  },
  [CHAT_MESSAGES_REQUEST_STARTED]: (state, seq) => {
    state.chatMessagesSeq = seq;
  },
  [CHAT_MESSAGES_REQUEST]: (state, { messages, seq, fresh }) => {
    state.chatMessages = mergeMessages(state.chatMessages, messages, { fresh });
    if (fresh) {
      state.chatMessagesAppliedSeq = seq;
    }
  },
  // Messages are only ever merged, so switching to another conversation has to clear
  // the previous one explicitly. Bumping the epoch also discards any request the
  // previous conversation still has in flight.
  [CHAT_MESSAGES_RESET]: (state) => {
    state.chatMessages = [];
    state.chatMessagesEpoch += 1;
  },
  [CHAT_MESSAGES_READ_REQUEST]: (state) => {
    state.chatMessages = state.chatMessages.map((message) => ({
      ...message,
      clientIsRead: true,
    }));
  },
  [CHAT_ATTACH_FILE]: (state, data) => {
    state.chatAttachedFiles.push(data);
  },
  [CHAT_CLEAR_ATTACHMENTS]: (state) => {
    state.chatAttachedFiles = [];
  },
  [CHAT_DELETE_ATTACHMENT]: (state, data) => {
    state.chatAttachedFiles = state.chatAttachedFiles.filter((file) => file.cdnFileId !== data.fileId);
  },
  [CHAT_WEBSOCKET_MESSAGE_READ_RECEIVED]: (state) => {
    state.chatMessages = state.chatMessages.map((message) => ({
      ...message,
      supportIsRead: true,
    }));
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
